import type { CatalogItem } from "@dishour/shared";
import { useState } from "react";
import { useCancelOrder, useCreateOrder, useMyOrders, useMySuppliers, useSupplierCatalog } from "../../api/orders.js";
import { AppShell } from "../../components/AppShell.js";
import { EmptyState } from "../../components/EmptyState.js";
import { OrderStatusBadge } from "../../components/StatusBadge.js";
import { QtyInput } from "../../components/QtyInput.js";
import { Skeleton } from "../../components/Skeleton.js";
import { cartLines, cartTotal, toOrderLines } from "../../lib/cart.js";
import { formatDateTime, formatMoney, formatQty } from "../../lib/format.js";

function SupplierPicker({ onSelect }: { onSelect: (id: string) => void }) {
  const suppliers = useMySuppliers();

  if (suppliers.isPending) return <Skeleton />;
  if (suppliers.error) {
    return (
      <p role="alert" className="text-red-600">
        목록을 불러오지 못했습니다.
      </p>
    );
  }
  if (!suppliers.data || suppliers.data.length === 0) {
    return <EmptyState message="연결된 공급업체가 없습니다. 관리자에게 문의해 주세요." />;
  }
  return (
    <ul>
      {suppliers.data.map((s) => (
        <li key={s.id} className="border-b border-stone-200">
          <button type="button" className="w-full py-3 text-left font-medium" onClick={() => onSelect(s.id)}>
            {s.name}
          </button>
        </li>
      ))}
    </ul>
  );
}

function CatalogAndCart({ supplierId, onBack }: { supplierId: string; onBack: () => void }) {
  const catalog = useSupplierCatalog(supplierId);
  const createOrder = useCreateOrder();
  const [cart, setCart] = useState<Record<string, string>>({});

  const items: CatalogItem[] = catalog.data ?? [];
  const lines = cartLines(cart, items);
  const total = cartTotal(lines);

  return (
    <div>
      <button type="button" className="mb-3 text-sm text-stone-500 underline" onClick={onBack}>
        다른 공급업체 선택
      </button>

      {catalog.isPending ? <p className="text-stone-500">불러오는 중…</p> : null}
      {catalog.data && catalog.data.length === 0 ? (
        <p className="py-8 text-center text-stone-500">주문 가능한 품목이 없습니다.</p>
      ) : null}

      <ul className="mb-24">
        {items.map((sku) => (
          <li key={sku.id} className="border-b border-stone-200 py-3">
            <div className="mb-2">
              <p className="font-medium">{sku.nameKo}</p>
              <p className="text-sm text-stone-500">
                {formatMoney(sku.unitPrice)} / {sku.unit}
                {sku.packSize ? ` · ${sku.packSize}` : ""}
              </p>
            </div>
            <QtyInput value={cart[sku.id] ?? ""} sku={sku} onChange={(next) => setCart({ ...cart, [sku.id]: next })} />
          </li>
        ))}
      </ul>

      {lines.length > 0 ? (
        <div className="fixed inset-x-0 bottom-16 z-10 mx-auto w-full max-w-screen-sm border-t border-stone-200 bg-white p-4 shadow-lg">
          <div className="mb-2 flex items-center justify-between text-sm text-stone-600">
            <span>{lines.length}개 품목</span>
            <span className="font-semibold text-stone-900">{formatMoney(total)}</span>
          </div>
          {createOrder.error ? (
            <p role="alert" className="mb-2 text-sm text-red-600">
              {(createOrder.error as Error).message}
            </p>
          ) : null}
          <button
            type="button"
            className="w-full rounded-lg bg-brand-700 px-4 py-3 font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            disabled={createOrder.isPending}
            onClick={() =>
              createOrder.mutate({ supplierId, lines: toOrderLines(lines) }, { onSuccess: () => setCart({}) })
            }
          >
            {createOrder.isPending ? "제출 중…" : `주문 제출 · ${formatMoney(total)}`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function OrderHistory() {
  const orders = useMyOrders();
  const cancelOrder = useCancelOrder();

  if (orders.isPending) return <Skeleton />;
  if (orders.error) {
    return (
      <p role="alert" className="text-red-600">
        목록을 불러오지 못했습니다.
      </p>
    );
  }
  if (!orders.data || orders.data.length === 0) {
    return <EmptyState message="아직 주문한 내역이 없습니다." />;
  }

  return (
    <ul>
      {orders.data.map((order) => (
        <li key={order.id} className="mb-4 rounded-xl border border-stone-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-medium">{order.supplier.name}</span>
            <OrderStatusBadge status={order.status} />
          </div>
          <ul className="mb-2 text-sm text-stone-600">
            {order.lines.map((line) => (
              <li key={line.id}>
                {line.sku.nameKo} · {formatQty(line.qty, line.sku)}
              </li>
            ))}
          </ul>
          <p className="mb-2 text-xs text-stone-400">{formatDateTime(order.requestedAt)}</p>
          {order.status === "rejected" && order.rejectionReason ? (
            <p className="mb-2 text-sm text-red-600">사유: {order.rejectionReason}</p>
          ) : null}
          {order.status === "pending" ? (
            <button
              type="button"
              className="rounded-lg border border-stone-300 px-3 py-2 text-sm disabled:opacity-50"
              disabled={cancelOrder.isPending}
              onClick={() => cancelOrder.mutate(order.id)}
            >
              주문 취소
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function RestaurantOrdersPage() {
  const [supplierId, setSupplierId] = useState<string | null>(null);

  return (
    <AppShell title="주문">
      <h2 className="mb-2 font-semibold">새 주문</h2>
      {supplierId ? (
        <CatalogAndCart supplierId={supplierId} onBack={() => setSupplierId(null)} />
      ) : (
        <SupplierPicker onSelect={setSupplierId} />
      )}
      <h2 className="mt-8 mb-2 font-semibold">주문 내역</h2>
      <OrderHistory />
    </AppShell>
  );
}
